/**
 * Shared utility for constructing authenticated document blob URLs
 *
 * Purpose:
 * - Constructs direct Azure Blob Storage URLs with fresh SAS tokens
 * - Avoids backend proxy overhead for document streaming
 * - Strips stale SAS tokens and appends fresh ones from cache
 *
 * Used by:
 * - forgeviewer.js (PDF viewer launcher)
 * - markupsPopup.vue (Markup editing)
 * - ViewersSection.vue (Dashboard embedded viewer)
 * - tags.js (OPEN_DOCUMENT_IN_VIEWER action)
 * - DocumentViewer.vue (PDF/Office viewer)
 */

import { getSasToken } from '@/services/sasTokenCache';
import documentVersionsService from '@/features/Documents/service/documentVersions.service';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

/**
 * Extract stable document ID from an attachment/document object
 * Handles different property names from various document sources
 *
 * @param {Object} attachment - Document object from API or store
 * @returns {string|null} - Stable document ID or null if not available
 */
export function getDocumentId(attachment) {
  if (!attachment) return null;
  // Priority: referenceDocumentId (set by action) > documentId > id
  return attachment.referenceDocumentId || attachment.documentId || attachment.id || null;
}

/**
 * Strip SAS token query parameters from an Azure Blob Storage URL.
 * Removes everything from `?sv=` onward (standard SAS format).
 *
 * @param {string} url - URL potentially containing a SAS token
 * @returns {string} - URL without SAS token
 */
export function stripSasToken(url) {
  if (!url) return url;

  // Standard format: ?sv=... (SAS is the first query param)
  const svIndex = url.indexOf('?sv=');
  if (svIndex !== -1) return url.substring(0, svIndex);

  // Alternate format: ?other=...&sv=... (SAS after other params)
  const ampSvIndex = url.indexOf('&sv=');
  if (ampSvIndex !== -1) return url.substring(0, ampSvIndex);

  return url;
}

/**
 * Construct a direct Azure Blob Storage URL with a fresh SAS token.
 * Fetches a cached SAS token and appends it to the document's storage URL.
 *
 * @param {Object} attachment - Document object with storageReference and file properties
 * @param {Object} options - Optional configuration
 * @param {boolean} options.strict - If true, return null when URL unavailable; else return attachment.url
 * @returns {Promise<string|null>} - Blob URL with SAS token, null, or original URL (loose mode)
 */
export async function getDocumentBlobUrl(attachment, options = {}) {
  const { strict = false } = options;

  if (!attachment) {
    return strict ? null : null;
  }

  // A non-current version has no storageReference of its own; stream it through the API.
  if (attachment.viewRevisionId) {
    return fetchVersionAsObjectUrl(
      getDocumentId(attachment),
      attachment.viewRevisionId,
      attachment.fileName || attachment.name,
      attachment.viewStorageReference,
    );
  }

  const storageRef = attachment.storageReference||attachment.url;

  if (!storageRef) {
    debugLog('[DocumentProxy]', 'No storageReference, returning fallback');
    return strict ? null : (attachment?.url || null);
  }

  // For temporary uploads, use storageReference as-is (already has valid SAS)
  if (attachment._isTemporary) {
    debugLog('[DocumentProxy]', 'Temporary upload, using storageReference directly');
    return storageRef;
  }

  try {
    const baseUrl = stripSasToken(storageRef);
    const sasToken = await getSasToken();
    const blobUrl = baseUrl + sasToken;

    debugLog('[DocumentProxy]', 'Blob URL constructed:', blobUrl.substring(0, 80) + '...');
    return blobUrl;
  } catch (err) {
    debugError('[DocumentProxy]', 'Failed to get SAS token, falling back to storageReference:', err);
    // Fallback: use storageReference as-is (may still have a valid SAS from BlobUrlSigningFilter)
    return storageRef;
  }
}

/**
 * Fetch a document from Azure Blob Storage and return a local blob: Object URL.
 *
 * Use this for viewers that modify URLs internally (Forge Viewer appends `?domain=`,
 * which corrupts SAS signatures). The returned blob: URL is local — viewers can
 * append whatever they want without breaking anything.
 *
 * IMPORTANT: The blob: URL persists until the page unloads or URL.revokeObjectURL()
 * is called. For single-document viewers, the previous blob is released when the
 * viewer unloads the model.
 *
 * @param {Object} attachment - Document object with storageReference
 * @param {Object} options - Optional configuration
 * @param {boolean} options.strict - If true, return null when URL unavailable
 * @returns {Promise<string|null>} - Local blob: URL, or null on failure
 */
export async function fetchDocumentAsObjectUrl(attachment, options = {}) {
  if (attachment?.viewRevisionId) {
    return fetchVersionAsObjectUrl(
      getDocumentId(attachment),
      attachment.viewRevisionId,
      attachment.fileName || attachment.name,
      attachment.viewStorageReference,
    );
  }
  const sasUrl = await getDocumentBlobUrl(attachment, options);
  if (!sasUrl) return null;

  try {
    debugLog('[DocumentProxy]', 'Fetching document as binary:', sasUrl.substring(0, 80) + '...');
    const response = await fetch(sasUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // Append filename as URL fragment so Forge Viewer can detect the file type.
    // Blob URLs support fragments per W3C File API — the fragment is stripped
    // before resolution, so the blob data is still found correctly.
    const fileName = attachment.fileName || attachment.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const hintedUrl = ext ? objectUrl + '#file.' + ext : objectUrl;

    debugLog('[DocumentProxy]', 'Object URL created:', hintedUrl);
    return hintedUrl;
  } catch (err) {
    debugError('[DocumentProxy]', 'Failed to fetch document as binary:', err);
    return null;
  }
}

// Versions are immutable, so a fetched version can be reused for the session. Least recently
// used first: revisionId -> { promise, size }. `size` is the blob's bytes once it has loaded.
const versionObjectUrls = new Map();
const MAX_CACHED_VERSIONS = 10;
// Drawings can be large; bound the memory held by cached blobs as well as their number.
const MAX_CACHED_VERSION_BYTES = 150 * 1024 * 1024;

function revokeCachedVersion(entry) {
  entry.promise.then((url) => url && URL.revokeObjectURL(url.split('#')[0]));
}

/**
 * Evicts the least recently used versions until the cache fits. `keep` (the entry being returned
 * to a caller) is never evicted, so its URL is not revoked under that caller.
 */
function trimVersionCache(keep) {
  let bytes = 0;
  versionObjectUrls.forEach((entry) => (bytes += entry.size || 0));
  for (const [id, entry] of Array.from(versionObjectUrls)) {
    if (versionObjectUrls.size <= MAX_CACHED_VERSIONS && bytes <= MAX_CACHED_VERSION_BYTES) break;
    if (entry === keep) continue;
    versionObjectUrls.delete(id);
    bytes -= entry.size || 0;
    revokeCachedVersion(entry);
  }
}

/** Revokes every cached version's Object URL, for when no viewer needs them any more. */
export function clearVersionCache() {
  versionObjectUrls.forEach(revokeCachedVersion);
  versionObjectUrls.clear();
}

/**
 * Fetch one document version and return a local blob: Object URL with a `#file.<ext>` hint, the
 * same shape as fetchDocumentAsObjectUrl. Used for versions other than the current one.
 *
 * The version's own file URL (`storageReference`, from GET api/documents/{id}/versions) is tried
 * first, the same way the current version is opened. GET api/documents/{id}/versions/{revisionId}/view
 * (bearer auth, streamed by the API) is the fallback, for rows without a URL or when the direct
 * read fails.
 *
 * @param {string} documentId
 * @param {string} revisionId
 * @param {string} fileName - used for the extension hint
 * @param {string|null} storageReference - the version's file URL, when known
 * @returns {Promise<string|null>}
 */
export function fetchVersionAsObjectUrl(documentId, revisionId, fileName = '', storageReference = null) {
  if (!documentId || !revisionId) return Promise.resolve(null);
  const cached = versionObjectUrls.get(revisionId);
  if (cached) {
    // Re-insert to mark it most recently used.
    versionObjectUrls.delete(revisionId);
    versionObjectUrls.set(revisionId, cached);
    return cached.promise;
  }

  const viaApi = () => documentVersionsService.fetchVersionBlob(documentId, revisionId);
  const direct = storageReference
    ? fetchBlobDirect(storageReference).catch((err) => {
        debugLog('[DocumentProxy]', 'Direct read of version failed, using the API:', err?.message);
        return viaApi();
      })
    : viaApi();

  const entry = { promise: null, size: 0 };
  entry.promise = direct
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      const ext = (fileName || '').split('.').pop()?.toLowerCase();
      debugLog('[DocumentProxy]', 'Version object URL created:', revisionId);
      entry.size = blob?.size || 0;
      // Only trim while still cached; an evicted or cleared entry is revoked by its evictor.
      if (versionObjectUrls.get(revisionId) === entry) trimVersionCache(entry);
      return ext ? objectUrl + '#file.' + ext : objectUrl;
    })
    .catch((err) => {
      if (versionObjectUrls.get(revisionId) === entry) versionObjectUrls.delete(revisionId);
      debugError('[DocumentProxy]', 'Failed to fetch document version:', err);
      return null;
    });

  versionObjectUrls.set(revisionId, entry);
  trimVersionCache(entry);
  return entry.promise;
}

/** Reads a stored file URL with a fresh SAS token, as the current version is read. Rejects on failure. */
async function fetchBlobDirect(storageReference) {
  const url = await getDocumentBlobUrl({ storageReference }, { strict: true });
  if (!url) throw new Error('No URL for the version');
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

/** Save one document version to disk under its file name. */
export async function downloadDocumentVersion(documentId, revisionId, fileName, storageReference = null) {
  const url = await fetchVersionAsObjectUrl(documentId, revisionId, fileName, storageReference);
  if (!url) return false;
  const link = document.createElement('a');
  link.href = url.split('#')[0];
  link.download = fileName || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}
